import { MfUser } from "./mfTypes";

export interface Dict<T> {
  [index: string]: T;
}

export interface SelectableItem<T> {
  item: T;
  selected: boolean;
}

export interface ApiCallOptions {
  withLoading?: boolean;
  customErrorsCB?: (status: number, response: any) => boolean;
}

export interface SfBreadcrumbRoute {
  label: string;
  url: string;
}

export type SfLoginScreen = "login" | "loggedin" | "forgot" | "sent";
export type LoadingStatus = "inited" | "loading" | "loaded" | "error";

export type SfFormFieldErrorCode =
  | "invalid"
  | "required"
  | "invalidEmail"
  | "match"
  | "minLength"
  | "minValue"
  | "exists";

export type SfAccessStatus =
  | "valid"
  | "loggedout"
  | "inactive"
  | "nosubject"
  | "unknownError";

export interface SfApiError {
  uid: number;
  status: number;
  code: number;
  message: string;
  name: string;
  type: string;
}

export type SfSizeOption = "sm" | "md" | "lg" | "full";

export interface SfFormField {
  value: any;
  touched?: boolean;
  errors?: SfFormFieldErrorCode[];
  required?: boolean;
  isEmail?: boolean;
  isDate?: boolean;
  trueRequired?: boolean;
  match?: string;
  minLength?: number; // min string length
  minValue?: number; // min number value
}

export interface SfFormData {
  fields: Dict<SfFormField>;
  isValid?: boolean;
}

export interface SfDateMarker {
  date: Date | string;
  type?: "dot" | "line";
  tooltip?: { text: string; color?: string; html?: string }[];
  color?: string;
}

export type SfAccountType = "individual" | "school" | "mf"; 

export type SfSubject = "spelling" | "maths" | "mfGame" | "mfWs";

export type SfUserRole =
  | "sfAdmin"
  | "sfEditor"
  | "sfGameDev"
  | "schoolAdmin"
  | "teacher"
  | "student"
  | "individualAdmin"
  | "individual"
  | "mfSchool"
  | "mfIndividual";

export type SfTestType =
  | "test"
  | "spellingRule"
  | "wordList"
  | "trickyWords"
  | "mathsObjective"
  | "mathsTest";

export const SfUserRoleId = {
  sfAdmin: 1,
  sfEditor: 5,
  sfGameDev: 6,
  schoolAdmin: 2,
  teacher: 3,
  student: 4,
  individualAdmin: 7,
  individual: 8,
  mfSchool: 9,
  mfIndividual: 10,
};

export interface SfNumberFormatter {
  format: (n: number) => string;
  parse: (s: string) => number;
  separators: { decimal: string; group: string };
}

export interface sfFieldFormatter {
  update: (value: string) => void;
  destroy: () => void;
}

export interface sfFieldAutofocus {
  destroy: () => void;
}

export interface SfGame {
  id: number;
  name: string;
  description: null | string;
  url: string;
  spelling: 0 | 1;
  maths: 0 | 1;
  activeLevels?: number[] | null;
  published: 0 | 1;
  is_free: 0 | 1;
  version: number;
  index_file?: string;
  game_dev_id?: number;
  createdon?: string;
  editedon?: string;
  createdby?: number;
  editedby?: number;
}

export interface SfSchoolSubscriptions {
  spelling: SfSubscription | null;
  maths: SfSubscription | null;
}

export interface SfIndividualSuibscriptions {
  spelling: SfSubscription | null;
  maths: SfSubscription | null;
  mathsws: SfSubscription | null;
}

export interface SfSchool {
  id: number;
  name: string;
  code: string;
  first_week_day: number;
  createdon: string;
  is_individual: boolean;
  postcode?: string;
  country_code?: string | null;
  city?: string;
  status?: number;
  year_start_month?: number;
  students_per_class_limit?: number;
  adminName?: string;
  isActive?: boolean;
  tax?: SfTax | null;
  subscriptions?: SfSchoolSubscriptions;
  futureSubscriptions?: SfSchoolSubscriptions;
  expireon?: string | null;
  classCountOverview?: {
    'limits': { spelling: number, maths: number },
    'classCount': {
      '1': { spelling: number, maths: number },
      '2': { spelling: number, maths: number },
    }
  }
}

export interface SfUser {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  roles: number[];
  email?: string;
  fullName: string;
  school_id: number | null;
  mathsObjectivesYear?: number | null;
  hasSpellingAccess: boolean;
  hasMathsAccess: boolean;
  hasMfGamesAccess: boolean;
  hasMfWsAccess: boolean;
  isActive?: boolean;
  realUserId?: number;
  school?: SfSchool | null;
  maths_level?: number;
  currentGroups?: SfStudentGroup[];
  currentClasses?: SfSchoolClass[];
  nextYearClasses?: SfSchoolClass[];
  teams?: {
    spelling: SfStudentTeam | null,
    maths: SfStudentTeam | null,
  };
  mfUser: MfUser | null;
  parent?: SfUser | null;
  individualSubscriptions?: SfIndividualSuibscriptions;
  individualFutureSubscriptions?: SfIndividualSuibscriptions;
}

export interface SfStudentTeam {
  id: number;
  name: string;
  subject: SfSubject;
  students?: SfUser[];
  studentCount?: number;
}

export interface SfStudentGroup {
  id: number;
  name: string;
  students?: SfUser[];
  studentCount?: number;
}

export interface SfSchoolClass {
  id: number;
  name: string;
  subject: SfSubject;
  year: number;
  active: 0 | 1;
  tricky_words: 0 | 1;
  maths_objectives_year: number;
  sf_points_limit: number;
  leaderboard_visible: boolean;
  teachers?: [SfUser];
  studentCount?: number;
  groupCount?: number;
  students?: SfUser[];
  testCount?: number;
  groups?: SfStudentGroup[];
  academicYearStart?: string;
  academicYearEnd?: string;
}

export interface SfPaginationData {
  currentPage: number;
  pageCount: number;
  perPage: number;
  totalCount: number;
}

export interface SfPropertyFilterOptions {
  filterEmptyString?: boolean;
  filterZero?: boolean;
  filterNull?: boolean;
  filterUndefined?: boolean;
  filterNegative?: boolean;
}

export interface SfSelectOption {
  label: string;
  key: string | number;
  value: any;
}

export interface SfLevelNode {
  id: number;
  parent_category_id: number | null;
  name: string;
  sort: number;
}

export interface SfTestResultInfo {
  wordCategoryId?: number;
  testId?: number;
  testResultId?: number;
  wordListId?: number;
  mathLevelId?: number;
  mathCustomLevelId?: number;
  mathTestId?: number;
  studentId?: number;
}

export interface SfCustomTest {
  id: number;
  name: string;
  displayName: string;
  description: string;
  publishon: string;
  deadline: string;
  status: number;
  deleted: boolean;
  schoolClasses?: SfSchoolClass[];
  studentGroups?: SfStudentGroup[];
  spellingPoints?: number;
  students?: SfUser[];
}

export interface SfUserPoints {
  week: number;
  month: number;
  year: number;
  total: number;
}

export type SfPurchaseType =
  | "individual"
  | "activation"
  | "addclasses"
  | "extend"
  | "payinvoice";

export interface SfCountry {
  id: number;
  countryCode: string;
  countryName: string;
  isoAlpha3: string;
}

export interface SfUSState {
  id: number;
  code: string;
  name: string;
}

export interface SfTax {
  name: string;
  value: number;
}

export type SfSubscriptionKind = "spelling" | "maths" | "mathsws";

export interface SfSubscription {
  id: number;
  main_subscription_id: number | null;
  kind: SfSubscriptionKind;
  school_id: number | null;
  individual_id: number | null;
  classCount: number;
  createdon: string;
  starton: string;
  is_trial: 1 | 0;
  transfered_from: "sf" | "mf" | null;
  expireon: string;
  active: boolean;
  status: "created" | "active" | "cancelled" | "invoice_expired";
  children: SfSubscription[];
  storeInvoice?: SfInvoice | null;
}

export interface SfStoreProduct {
  id: number;
  name: string;
  label: string;
  cost: number;
}

export interface SfStoreDiscount {
  id: number;
  name: string;
  label: string;
  description: string;
  discount: number;
}

export interface SfCostPeriod {
  start: string;
  end: string;
  ratio: number;
}

export interface SfStoreSubscriptionsCost {
  subscriptions: {[index: string]: {
    name: string,
    kind: SfSubscriptionKind,
    label: string;
    cost: number;
    period: SfCostPeriod | null;
  }};
  comboDiscount: {
    discount: number,
    label: string,
  };
  availableTrial?: SfTrialData | null;
}

export interface SfStorePurchaseProduct {
  name: string;
  kind: SfSubscriptionKind;
  label: string;
  count: number;
  cost: number;
  period: SfCostPeriod;
  total: number;
}

export interface SfStorePurchaseData {
  products: SfStorePurchaseProduct[];
  discounts: { name: string; count: number; discount: number; total: number }[];
  tax: SfTax | null;
  taxTotal: number;
  subtotal: number;
  total: number;
  availableTrial?: SfTrialData | null;
}

export interface SfStorePurchase {
  id: number;
  school_id: number;
  user_id: number;
  kind: SfPurchaseType;
  payment_method: number;
  status: number;
  purchaseData: SfStorePurchaseData;
}

export interface SfPurchaseResume {
  school_id: number;
  subTotal: number;
  total: number;
  classCount: number;
  productCost: number;
  kind: SfPurchaseType;
}

export interface SfInvoice {
  id: number;
  school_reference: string;
  reference: string;
  status: number;
  createdon: string;
  deadline: string;
  storePurchase: SfStorePurchase;
  last_senton: string;
  paidon: string;
  school?: SfSchool;
  version: number;
}

export interface SfLeadboardData {
  id: number;
  first_name: string;
  last_name: string;
  sf_points: number;
}

export interface SfTimeRange {
  value: number;
  label: string;
  from: string;
  to?: string;
}

export interface SfClassStats {
  id: number;
  nameSort: string;
  practiceCount: number;
  testAvg: number;
  testCount: number;
  user: SfUser;
}

export interface SfUserAlertMessage {
  id: number;
  filter_code: string;
  title: string;
  message: string;
  publishon: string;
  publishoff: string;
  show_on_top: 0 | 1;
  show_on_dashboard: 0 | 1;
  priority: number;
  roles: number[];
}

export interface SfTrialData {
  type: "school" | "spelling" | "maths" | "individual";
  length: number;
}
